import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  Divider,
  useTheme,
  Theme,
  CircularProgress,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import CustomTypography from "../form/CustomTypography";
import { observer } from "mobx-react";
import { formatDate, formatDateWithoutTime } from "../functions/formatDate";
import { ActivityLog } from "../../../../models/Activity/ActivityLog";

interface ActivityLogDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activityLogs: ActivityLog[];
  isLoading?: boolean;
  title?: string;
  subtitle?: string | null;
}

const VARIANT_DRAWER_Z_OFFSET = 2000;

const groupByDate = (logs: ActivityLog[]) => {
  const grouped: Record<string, ActivityLog[]> = {};

  logs.forEach((log) => {
    const date = formatDateWithoutTime(log.created_at, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    if (!grouped[date]) {
      grouped[date] = [];
    }

    grouped[date].push({
      ...log,
      activity: log.activity?.replace(
        "#date_time#",
        formatDate(log.created_at),
      ),
    });
  });

  return grouped;
};

const ActivityLogDrawer: React.FC<ActivityLogDrawerProps> = observer(
  ({
    isOpen,
    onClose,
    activityLogs,
    isLoading = false,
    title = "Activity History",
    subtitle,
  }) => {
    const theme = useTheme<Theme>();

    const groupedLogs = groupByDate(activityLogs);
    const sortedDates = Object.keys(groupedLogs).sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    );

    const getInitials = (name: string) => {
      if (!name) return "?";
      const parts = name.split(" ");
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name[0].toUpperCase();
    };

    const getAvatarColor = (name: string) => {
      const colors = [
        "#1976d2",
        "#388e3c",
        "#d32f2f",
        "#7b1fa2",
        "#f57c00",
        "#0288d1",
        "#689f38",
        "#e64a19",
      ];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    };

    return (
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            height: "100vh",
            boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.12)",
            display: "flex",
            flexDirection: "column",
          },
        }}
        ModalProps={{
          keepMounted: true,
          sx: {
            zIndex: (muiTheme) =>
              muiTheme.zIndex.drawer + VARIANT_DRAWER_Z_OFFSET,
          },
          slotProps: {
            backdrop: {
              sx: {
                zIndex: (muiTheme) =>
                  muiTheme.zIndex.drawer + VARIANT_DRAWER_Z_OFFSET - 1,
              },
            },
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            pb: 2,
            zIndex: 1,
            backgroundColor: "background.paper",
            width: {
              xl: "420px",
              lg: "400px",
              md: "380px",
              sm: "340px",
              xs: "300px",
            },
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: theme.palette.grey[100],
              "&:hover": {
                backgroundColor: theme.palette.grey[200],
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                backgroundColor: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HistoryIcon sx={{ color: "white", fontSize: 22 }} />
            </Box>
            <Box>
              <CustomTypography
                variant="heading5"
                style={{ marginBottom: "3px" }}
              >
                {title}
              </CustomTypography>
              {subtitle && (
                <CustomTypography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {subtitle}
                </CustomTypography>
              )}
            </Box>
          </Box>
          <Divider sx={{ mt: 2.5 }} />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 3,
            pb: 3,
            width: {
              xl: "420px",
              lg: "400px",
              md: "380px",
              sm: "340px",
              xs: "300px",
            },
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : activityLogs.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "200px",
                color: theme.palette.text.secondary,
              }}
            >
              <HistoryIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <CustomTypography variant="body1">
                No activity records found
              </CustomTypography>
            </Box>
          ) : (
            sortedDates.map((date) => (
              <Box key={date} sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                    mt: 2,
                  }}
                >
                  <Box
                    sx={{
                      height: "1px",
                      flex: 1,
                      backgroundColor: theme.palette.divider,
                    }}
                  />
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      px: 2,
                      py: 0.5,
                      backgroundColor: theme.palette.grey[100],
                      borderRadius: "12px",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {formatDateWithoutTime(date, {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </CustomTypography>
                  <Box
                    sx={{
                      height: "1px",
                      flex: 1,
                      backgroundColor: theme.palette.divider,
                    }}
                  />
                </Box>

                {groupedLogs[date].map((log, index) => (
                  <Box
                    key={log.id}
                    sx={{
                      display: "flex",
                      gap: 1,
                      position: "relative",
                      pb: index < groupedLogs[date].length - 1 ? 2 : 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          backgroundColor: getAvatarColor(
                            log.created_by_name || "",
                          ),
                          marginTop: 1,
                        }}
                      >
                        {getInitials(log.created_by_name || "")}
                      </Avatar>
                      {index < groupedLogs[date].length - 1 && (
                        <Box
                          sx={{
                            width: "2px",
                            flex: 1,
                            backgroundColor: theme.palette.divider,
                            mt: 1,
                            minHeight: "20px",
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          backgroundColor: theme.palette.grey[50],
                          borderRadius: "4px",
                          p: 1,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <CustomTypography
                          variant="body2"
                          style={{
                            color: theme.palette.text.primary,
                            lineHeight: 1.4,
                          }}
                        >
                          {log.activity}
                        </CustomTypography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))
          )}
        </Box>
      </Drawer>
    );
  },
);

export default ActivityLogDrawer;
